// TelcoTrack - Polygon Service
// Service for polygon API communication

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, finalize, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import {
  Polygon,
  PolygonQueryDto,
  PolygonResponse,
  PolygonProgressSnapshot,
  MapPolygon,
  PolygonResponsiblePerson,
  PolygonCommercialItem,
} from '../models/polygon.model';
import { getStatusColor } from '../../shared/utils/object.utils';

export interface ProjectDriveStatus {
  enabled: boolean;
  configured: boolean;
  syncStatus: 'pending_identity' | 'queued' | 'synced' | 'error' | 'disabled';
  projectFolderId: string | null;
  projectFolderUrl: string | null;
  lastReconciledAt: string | null;
  lastError: string | null;
  waitingFor: string[];
  jobId?: string | null;
  contentBackfill?: {
    jobId: string | null;
    status: string | null;
    finishedAt: string | null;
    result: Record<string, unknown> | null;
  };
}

/** Položka v prohlížeči složky stavby na sdíleném disku Zakázky. */
export interface ProjectDriveBrowseItem {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  webViewLink: string | null;
  size: number | null;
}

/** Poznámka na přehledu polygonu. Autor je uživatel ze session. */
export interface PolygonNote {
  id: string;
  polygonId: string;
  text: string;
  createdAt: string;
  authorUserId: string;
  authorName: string | null;
}

/** Výsledek listingu aktuální složky stavby na sdíleném disku. */
export interface ProjectDriveBrowseResult {
  polygonId: string;
  projectFolderId: string;
  projectFolderUrl: string | null;
  folderId: string;
  folderName: string;
  parentFolderId: string | null;
  items: ProjectDriveBrowseItem[];
}

@Injectable({
  providedIn: 'root'
})
export class PolygonService {
  private readonly apiUrl = `${environment.apiUrl}${environment.polygonServiceUrl}`;
  private readonly objectsApiUrl = `${environment.apiUrl}${environment.objectServiceUrl}`;

  /** Session cache + in-flight dedup for list GET (map, dropdowns, dashboard). */
  private readonly polygonListInflight = new Map<string, Observable<Polygon[]>>();
  private readonly polygonListCache = new Map<string, { expiresAt: number; data: Polygon[] }>();

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService
  ) {}

  /**
   * Active construction folders only (`planState=aktivni` on API).
   * Use for polygon dropdowns and navigation lists.
   */
  getActivePolygons(
    query?: Omit<PolygonQueryDto, 'planState'> & {
      /** Map layer: boundaries + status. Default false for dropdowns. */
      forMap?: boolean;
      /** Table/Gantt cards: object aggregates without geometry. */
      withDerivedStats?: boolean;
    },
  ): Observable<Polygon[]> {
    const { forMap, withDerivedStats, ...rest } = query ?? {};
    return this.getAllPolygons({
      ...rest,
      planState: 'aktivni',
      limit: rest.limit ?? 1000,
      includeBoundary: forMap ? true : false,
      includeDerivedStats: withDerivedStats ?? false,
    });
  }

  /** Map polygon layer — boundaries + status, no object aggregates. */
  getActivePolygonsForMap(query?: Omit<PolygonQueryDto, 'planState' | 'includeBoundary' | 'includeDerivedStats'>): Observable<Polygon[]> {
    return this.getActivePolygons({ ...query, forMap: true });
  }

  /**
   * Map layer + unified search — all plan statuses (bez `planState`), s hranicí.
   * Visibility on the map is filtered client-side by plan-status tabs.
   */
  getPolygonsForMapSearch(
    query?: Omit<PolygonQueryDto, 'planState' | 'includeBoundary' | 'includeDerivedStats'>,
  ): Observable<Polygon[]> {
    return this.getAllPolygons({
      ...query,
      limit: query?.limit ?? 2000,
      includeBoundary: true,
      includeDerivedStats: false,
    });
  }

  /** Polygon table / dashboard cards — aggregates without geometry. */
  getPolygonsForList(query?: PolygonQueryDto): Observable<Polygon[]> {
    return this.getAllPolygons({
      includeBoundary: false,
      includeDerivedStats: true,
      ...query,
    });
  }

  /** Dropdowns / filters — id, name, kia, columns. No object scan. */
  getPolygonsForPicker(query?: Omit<PolygonQueryDto, 'includeBoundary' | 'includeDerivedStats'>): Observable<Polygon[]> {
    return this.getAllPolygons({
      ...query,
      limit: query?.limit ?? 1000,
      includeBoundary: false,
      includeDerivedStats: false,
    });
  }

  /** Drop cached polygon lists after create/update/delete/status recalc. */
  invalidatePolygonListCache(): void {
    this.polygonListCache.clear();
  }

  /**
   * Get all polygons with filtering and pagination
   */
  getAllPolygons(query?: PolygonQueryDto): Observable<Polygon[]> {
    const cacheKey = this.polygonListCacheKey(query);
    const cached = this.polygonListCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.data);
    }

    const inflight = this.polygonListInflight.get(cacheKey);
    if (inflight) return inflight;

    let params = new HttpParams();

    if (query) {
      Object.keys(query).forEach(key => {
        const value = query[key as keyof PolygonQueryDto];
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, value.toString());
        }
      });
    }

    const req = this.http.get<any>(`${this.apiUrl}`, { params })
      .pipe(
        map(response => {
          if (response.success && response.data && Array.isArray(response.data.polygons)) {
            return response.data.polygons;
          }
          this.logger.warn('Unexpected polygon response format:', response);
          return [];
        }),
        tap(polygons => {
          this.polygonListCache.set(cacheKey, {
            expiresAt: Date.now() + this.polygonListCacheTtlMs(),
            data: polygons,
          });
        }),
        catchError(this.handleError),
        finalize(() => {
          if (this.polygonListInflight.get(cacheKey) === req) {
            this.polygonListInflight.delete(cacheKey);
          }
        }),
      );
    this.polygonListInflight.set(cacheKey, req);
    return req;
  }

  private polygonListCacheTtlMs(): number {
    return environment.performance?.cacheTimeout ?? 5 * 60 * 1000;
  }

  private polygonListCacheKey(query?: PolygonQueryDto): string {
    if (!query) return '{}';
    const normalized: Record<string, string> = {};
    for (const key of Object.keys(query).sort()) {
      const value = query[key as keyof PolygonQueryDto];
      if (value !== undefined && value !== null && value !== '') {
        normalized[key] = String(value);
      }
    }
    return JSON.stringify(normalized);
  }

  /**
   * Create polygon. Superadmin-only on the API; used by the Import administration page.
   */
  createPolygon(
    body: Pick<Polygon, 'name'> &
      Partial<Pick<Polygon, 'description' | 'status' | 'kiaCode' | 'realizationYear' | 'folderNumber' | 'constructionType' | 'responsibleBranch' | 'expectedConstructionStart' | 'plannedConstructionEnd'>> & {
        boundary: { type: 'Polygon'; coordinates: number[][][] };
        metadata?: Partial<Polygon['metadata']>;
      },
  ): Observable<Polygon> {
    return this.http.post<PolygonResponse>(`${this.apiUrl}`, body)
      .pipe(
        tap(() => this.invalidatePolygonListCache()),
        map(response => {
          if (response.success && response.data && !Array.isArray(response.data)) {
            return response.data as Polygon;
          }
          throw new Error('Create failed');
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Trigger recalculation of polygon status (based on object statuses).
   * Returns the updated polygon; call after object status change to refresh UI.
   */
  recalculateStatus(polygonId: string): Observable<Polygon> {
    return this.http.post<{ success: boolean; data?: Polygon }>(`${this.apiUrl}/${polygonId}/recalculate-status`, {}).pipe(
      tap(() => this.invalidatePolygonListCache()),
      map(response => {
        if (response?.success && response.data) {
          return response.data;
        }
        throw new Error('Recalculate failed');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update polygon (e.g. name, description, status, kiaCode).
   */
  updatePolygon(
    id: string,
    body: Partial<Pick<Polygon, 'name' | 'description' | 'status' | 'kiaCode' | 'realizationYear' | 'folderNumber' | 'constructionType' | 'responsibleBranch' | 'expectedConstructionStart' | 'plannedConstructionEnd' | 'metadata'>>,
  ): Observable<Polygon> {
    return this.http.put<PolygonResponse>(`${this.apiUrl}/${id}`, body)
      .pipe(
        tap(() => this.invalidatePolygonListCache()),
        map(response => {
          if (response.success && response.data && !Array.isArray(response.data)) {
            return response.data as Polygon;
          }
          throw new Error('Update failed');
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Delete polygon (soft delete). Call only after deleting all objects in polygon (admin flow).
   */
  deletePolygon(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`)
      .pipe(
        tap(() => this.invalidatePolygonListCache()),
        catchError(this.handleError),
      );
  }

  /**
   * Uložení polygonového ceníku výkazu práce (admin/superadmin).
   */
  updateWorkReportPriceOverrides(id: string, workReportPriceOverrides: Record<string, number>): Observable<Polygon> {
    return this.http
      .patch<PolygonResponse>(`${this.apiUrl}/${id}/work-report-price-overrides`, { workReportPriceOverrides })
      .pipe(
        map((response) => {
          if (response.success && response.data && !Array.isArray(response.data)) {
            return response.data as Polygon;
          }
          throw new Error('Update price overrides failed');
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Get polygon by ID
   */
  getPolygonById(id: string, query?: Pick<PolygonQueryDto, 'includeLifecycle'>): Observable<Polygon> {
    let params = new HttpParams();
    if (query?.includeLifecycle !== undefined && query.includeLifecycle !== null && query.includeLifecycle !== '') {
      params = params.set('includeLifecycle', String(query.includeLifecycle));
    }

    return this.http.get<PolygonResponse>(`${this.apiUrl}/${id}`, { params })
      .pipe(
        map(response => {
          if (response.success && !Array.isArray(response.data)) {
            return response.data as Polygon;
          }
          throw new Error('Polygon not found');
        }),
        catchError(this.handleError)
      );
  }

  /** Hotovost stavby — bez polygonId součet všech polygonů. */
  getProgressSnapshot(
    polygonId?: string | null,
    opts?: { dateFrom?: string; dateTo?: string },
  ): Observable<PolygonProgressSnapshot> {
    let params = new HttpParams();
    const pid = polygonId?.trim();
    if (pid) params = params.set('polygonId', pid);
    const dateFrom = opts?.dateFrom?.trim();
    const dateTo = opts?.dateTo?.trim();
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    return this.http.get<any>(`${this.apiUrl}/progress-snapshot`, { params }).pipe(
      map((response) => {
        if (response?.success) return response.data as PolygonProgressSnapshot;
        throw new Error('Failed to load progress snapshot');
      }),
      catchError(this.handleError),
    );
  }

  getResponsiblePersons(polygonId: string): Observable<PolygonResponsiblePerson[]> {
    return this.http.get<any>(`${this.apiUrl}/${polygonId}/responsible-persons`).pipe(
      map((response) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data as PolygonResponsiblePerson[];
        }
        return [];
      }),
      catchError(this.handleError),
    );
  }

  getCommercialItems(polygonId: string): Observable<PolygonCommercialItem[]> {
    return this.http.get<any>(`${this.apiUrl}/${polygonId}/commercial-items`).pipe(
      map((response) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data as PolygonCommercialItem[];
        }
        return [];
      }),
      catchError(this.handleError),
    );
  }

  updateCommercialItems(polygonId: string, items: PolygonCommercialItem[]): Observable<PolygonCommercialItem[]> {
    // BE DTO `UpdatePolygonCommercialItemsDto` má `forbidNonWhitelisted: true`, takže extra pole
    // z entity (`polygonId`, `createdAt`, `updatedAt`) shodí validation pipe. TypeScript cast
    // při `getCommercialItems` neodstraní runtime pole — explicitní pick je nutný.
    const payload = items.map((item) => this.toCommercialItemPayload(item));
    return this.http.patch<any>(`${this.apiUrl}/${polygonId}/commercial-items`, { items: payload }).pipe(
      map((response) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data as PolygonCommercialItem[];
        }
        throw new Error('Update commercial items failed');
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Whitelistuje pole tak, aby přesně matchovala BE `PolygonCommercialItemDto`.
   * Drží se DTO (ne entity) — `polygonId`, `createdAt`, `updatedAt` se nikdy neposílají zpět.
   */
  private toCommercialItemPayload(item: PolygonCommercialItem): PolygonCommercialItem {
    return {
      id: item.id,
      sortOrder: item.sortOrder,
      workScope: item.workScope,
      workCode: item.workCode,
      businessStatus: item.businessStatus,
      sppCode: item.sppCode,
      orderNumber: item.orderNumber,
      orderAmount: item.orderAmount,
      orderDate: item.orderDate,
      billingRounds: item.billingRounds,
      amountDifference: item.amountDifference,
      note: item.note,
      noteUpdatedAt: item.noteUpdatedAt,
    };
  }

  /**
   * Find polygons containing a point
   */
  findPolygonsContainingPoint(lng: number, lat: number): Observable<Polygon[]> {
    const params = new HttpParams()
      .set('lng', lng.toString())
      .set('lat', lat.toString());

    return this.http.get<PolygonResponse>(`${this.apiUrl}/spatial/containing-point`, { params })
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.data)) {
            return response.data;
          }
          return [];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Find polygons within radius of point
   */
  findPolygonsWithinRadius(lng: number, lat: number, radiusMeters: number): Observable<Polygon[]> {
    const params = new HttpParams()
      .set('lng', lng.toString())
      .set('lat', lat.toString())
      .set('radius', radiusMeters.toString());

    return this.http.get<PolygonResponse>(`${this.apiUrl}/spatial/within-radius`, { params })
      .pipe(
        map(response => {
          if (response.success && Array.isArray(response.data)) {
            return response.data;
          }
          return [];
        }),
        catchError(this.handleError)
      );
  }


  /**
   * Convert polygon to map polygon for display
   */
  convertToMapPolygon(polygon: Polygon): MapPolygon {
    const boundaryPath = this.extractBoundaryPath(polygon);

    return {
      id: polygon.id,
      name: polygon.name,
      kiaCode: polygon.kiaCode,
      status: polygon.status,
      constructionType: polygon.constructionType ?? null,
      coordinates: [Number.parseFloat(polygon.centroidLng?.toString() || '0'), Number.parseFloat(polygon.centroidLat?.toString() || '0')],
      color: getStatusColor(polygon.status),
      boundary: polygon.boundary,
      boundaryPath: boundaryPath,
      objectCount: polygon.objectCount || 0,
    };
  }

  /**
   * Extract boundary path from GeoJSON boundary
   */
  private extractBoundaryPath(polygon: Polygon): [number, number][] | undefined {
    if (!polygon.boundary) {
      this.logger.warn(`Polygon ${polygon.name} has no boundary data`);
      return undefined;
    }

    const boundary = typeof polygon.boundary === 'string' ? JSON.parse(polygon.boundary) : polygon.boundary;
    
    if (boundary.type === 'Polygon' && Array.isArray(boundary.coordinates)) {
      return this.extractPolygonPath(boundary.coordinates[0]);
    }
    
    if (boundary.type === 'MultiPolygon' && Array.isArray(boundary.coordinates)) {
      const firstPolygon = boundary.coordinates[0];
      if (Array.isArray(firstPolygon) && Array.isArray(firstPolygon[0])) {
        return this.extractPolygonPath(firstPolygon[0]);
      }
    }
    
    this.logger.warn(`Unknown boundary type for polygon ${polygon.name}:`, boundary);
    return undefined;
  }

  /**
   * Extract path from polygon ring and ensure it's closed
   */
  private extractPolygonPath(outerRing: number[][]): [number, number][] | undefined {
    if (!Array.isArray(outerRing)) {
      return undefined;
    }

    const boundaryPath = outerRing.map((coord: number[]) => [coord[0], coord[1]] as [number, number]);
    
    if (boundaryPath.length === 0) {
      return undefined;
    }

    const firstPoint = boundaryPath[0];
    const lastPoint = boundaryPath.at(-1)!;
    
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
      boundaryPath.push([firstPoint[0], firstPoint[1]]);
    }
    
    return boundaryPath;
  }

  getProjectDriveStatus(polygonId: string): Observable<ProjectDriveStatus> {
    return this.http
      .get<{ success?: boolean; data?: ProjectDriveStatus }>(
        `${this.objectsApiUrl}/project-drive/${encodeURIComponent(polygonId)}`,
      )
      .pipe(
        map((response) => {
          if (response.success && response.data) return response.data;
          throw new Error('Project Drive status failed');
        }),
        catchError(this.handleError),
      );
  }

  /** Soubory a podsložky ve složce stavby (kořen = složka stavby; `folderId` = vnořená). */
  browseProjectDrive(
    polygonId: string,
    folderId?: string | null,
  ): Observable<ProjectDriveBrowseResult> {
    let params = new HttpParams();
    const id = (folderId ?? '').trim();
    if (id) params = params.set('folderId', id);
    return this.http
      .get<{ success?: boolean; data?: ProjectDriveBrowseResult }>(
        `${this.objectsApiUrl}/project-drive/${encodeURIComponent(polygonId)}/children`,
        { params },
      )
      .pipe(
        map((response) => {
          if (response.success && response.data) return response.data;
          throw new Error('Project Drive browse failed');
        }),
        catchError(this.handleError),
      );
  }

  /** Nahrát soubor do složky stavby na sdíleném disku Zakázky. */
  uploadProjectDriveFile(
    polygonId: string,
    file: File,
    folderId?: string | null,
  ): Observable<ProjectDriveBrowseItem> {
    let params = new HttpParams();
    const id = (folderId ?? '').trim();
    if (id) params = params.set('folderId', id);
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<{ success?: boolean; data?: ProjectDriveBrowseItem }>(
        `${this.objectsApiUrl}/project-drive/${encodeURIComponent(polygonId)}/upload`,
        formData,
        { params },
      )
      .pipe(
        map((response) => {
          if (response.success && response.data) return response.data;
          throw new Error('Project Drive upload failed');
        }),
        catchError(this.handleError),
      );
  }

  reconcileProjectDrive(polygonId: string): Observable<ProjectDriveStatus> {
    return this.http
      .post<{ success?: boolean; data?: ProjectDriveStatus }>(
        `${this.objectsApiUrl}/project-drive/${encodeURIComponent(polygonId)}/reconcile`,
        {},
      )
      .pipe(
        map((response) => {
          if (response.success && response.data) return response.data;
          throw new Error('Project Drive reconcile failed');
        }),
        catchError(this.handleError),
      );
  }

  backfillProjectDrive(polygonId: string): Observable<ProjectDriveStatus> {
    return this.http
      .post<{ success?: boolean; data?: ProjectDriveStatus }>(
        `${this.objectsApiUrl}/project-drive/${encodeURIComponent(polygonId)}/backfill`,
        {},
      )
      .pipe(
        map((response) => {
          if (response.success && response.data) return response.data;
          throw new Error('Project Drive backfill failed');
        }),
        catchError(this.handleError),
      );
  }

  listPolygonNotes(polygonId: string): Observable<PolygonNote[]> {
    return this.http.get<{ success?: boolean; data?: PolygonNote[] }>(`${this.apiUrl}/${polygonId}/notes`).pipe(
      map((response) => {
        if (response?.success && Array.isArray(response.data)) return response.data;
        throw new Error('Poznámky se nepodařilo načíst.');
      }),
      catchError(this.handleError),
    );
  }

  addPolygonNote(polygonId: string, text: string): Observable<PolygonNote> {
    return this.http
      .post<{ success?: boolean; data?: PolygonNote }>(`${this.apiUrl}/${polygonId}/notes`, { text })
      .pipe(
        map((response) => {
          if (response?.success && response.data && !Array.isArray(response.data)) return response.data;
          throw new Error('Poznámku se nepodařilo uložit.');
        }),
        catchError(this.handleError),
      );
  }

  updatePolygonNote(polygonId: string, noteId: string, text: string): Observable<PolygonNote> {
    return this.http
      .patch<{ success?: boolean; data?: PolygonNote }>(`${this.apiUrl}/${polygonId}/notes/${noteId}`, { text })
      .pipe(
        map((response) => {
          if (response?.success && response.data && !Array.isArray(response.data)) return response.data;
          throw new Error('Poznámku se nepodařilo upravit.');
        }),
        catchError(this.handleError),
      );
  }

  deletePolygonNote(polygonId: string, noteId: string): Observable<void> {
    return this.http
      .delete<{ success?: boolean }>(`${this.apiUrl}/${polygonId}/notes/${noteId}`)
      .pipe(
        map((response) => {
          if (response?.success) return;
          throw new Error('Poznámku se nepodařilo smazat.');
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Handle API errors
   */
  private readonly handleError = (error: any): Observable<never> => {
    this.logger.error('Polygon API Error:', error);
    
    let errorMessage = 'An error occurred while loading polygons';
    
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    const thrown = new Error(errorMessage) as Error & { status?: number };
    thrown.status = typeof error?.status === 'number' ? error.status : undefined;
    return throwError(() => thrown);
  }
}
